import React, {useEffect, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTimes, faArrowLeft} from '@fortawesome/free-solid-svg-icons';
import {useBodyScrollLock} from '../../hooks/useBodyScrollLock';
import {useOverlayBackButton} from '../../hooks/useOverlayBackButton';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    showHeader?: boolean;
    title?: string;
    subtitle?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    /**
     * Cách lớp phủ trình bày TRÊN ĐIỆN THOẠI (từ `sm` trở lên luôn là thẻ nổi
     * giữa màn hình, không đổi):
     *
     *   fullscreen - chiếm trọn màn + mũi tên quay lại. Cho nội dung dài: form
     *                nhập chỉ số, hóa đơn, lịch sử. Mặc định.
     *   sheet      - trượt lên từ đáy, bo góc trên, cao tối đa 90%. Cho nội dung
     *                ngắn cần trả lời nhanh rồi quay lại chỗ cũ. Đây là thao tác
     *                quen tay nhất trên điện thoại: ngón cái ở gần đáy màn.
     *   dialog     - thẻ nổi giữa màn ngay cả trên điện thoại. Cho hộp xác nhận
     *                vài dòng, phóng to lên thành cả màn hình thì lố.
     *
     * Trước đây chỗ này là cờ `mobileFullScreen` hai trạng thái, nên "trượt lên
     * từ đáy" không diễn đạt được - và các component tên `*Sheet` thật ra vẫn
     * mở ra dạng toàn màn hình, đúng cái tên của chúng nói là không phải.
     */
    mobile?: 'fullscreen' | 'sheet' | 'dialog';
    // Nút/nội dung phụ đặt bên phải thanh tiêu đề (vd: nút Chia sẻ)
    headerActions?: React.ReactNode;
    // Thanh hành động dính đáy màn hình (vd: nút Lưu / Xác nhận)
    footer?: React.ReactNode;
    // Bỏ padding mặc định của phần thân (nội dung tự lo padding)
    bare?: boolean;
    /**
     * Tầng của lớp phủ.
     *
     *   base - mặc định, z-100. Cùng tầng với thư viện hóa đơn, bảng nhập
     *          hàng loạt và các lớp phủ tự dựng khác.
     *   top  - z-120, trên cả ImageViewer (z-110). Dành cho hộp xác nhận: nó
     *          mở CHỒNG LÊN một lớp phủ khác (hỏi trước khi đóng bảng nhập,
     *          trước khi xoá ảnh trong thư viện) nên không được nằm dưới.
     *
     * Không dựa vào thứ tự portal: hai lớp cùng z-index thì lớp mở sau nằm
     * trên, nhưng đó là hệ quả của thứ tự chèn vào DOM - đúng cho tới khi có
     * ai đó render sẵn một lớp phủ đóng.
     */
    layer?: 'base' | 'top';
    /** Nút X nổi ở góc (chỉ có khi không dùng thanh tiêu đề). Tắt khi hộp
     *  thoại đã có nút Hủy riêng, để khỏi có hai đường đóng cạnh nhau. */
    showClose?: boolean;
    /** id của phần tử làm tiêu đề, khi tiêu đề nằm trong `children` */
    labelledBy?: string;
}

/**
 * Thanh tiêu đề của một lớp phủ toàn màn hình.
 *
 * Tách riêng khỏi <Modal> vì có những màn KHÔNG dùng được khung của Modal -
 * bảng nhập hàng loạt cần chiều cao cố định cho lưới hai cột, thư viện hóa đơn
 * cần nền đặc tràn viền - nhưng thanh tiêu đề thì vẫn phải giống hệt: mũi tên
 * quay lại trên điện thoại, dấu X ở góc phải trên máy tính.
 *
 * Trước đây ba màn đó chép lại đoạn này, và mỗi bản lệch một kiểu (chỗ thì nút
 * đóng bị vô hiệu lúc đang lưu, chỗ thì không; chỗ có aria-label, chỗ không).
 */
export const ModalHeader: React.FC<{
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    onClose: () => void;
    /** Chặn đóng trong lúc đang lưu - tránh mất dữ liệu giữa chừng */
    closeDisabled?: boolean;
    /** Nút phụ đặt giữa tiêu đề và nút đóng (Chia sẻ, Lịch sử…) */
    actions?: React.ReactNode;
    titleId?: string;
}> = ({title, subtitle, onClose, closeDisabled = false, actions, titleId}) => (
    <div className="flex items-center gap-3 px-3 sm:px-6 py-3 sm:py-4
        border-b border-slate-200/70 dark:border-white/10 flex-shrink-0">
        {/* Mobile: mũi tên quay lại - thao tác quen thuộc trên điện thoại */}
        <button
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Quay lại"
            className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full
                text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10
                transition-colors flex-shrink-0 disabled:opacity-50"
        >
            <FontAwesomeIcon icon={faArrowLeft}/>
        </button>

        <div className="min-w-0 flex-1">
            <h3 id={titleId} className="text-lg sm:text-xl font-semibold text-slate-800 dark:text-slate-100 truncate">
                {title}
            </h3>
            {subtitle && (
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">
                    {subtitle}
                </p>
            )}
        </div>

        {actions}

        {/* Desktop: nút đóng dạng X */}
        <button
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Đóng"
            className="hidden sm:flex w-9 h-9 items-center justify-center flex-shrink-0
                rounded-full glass-btn text-slate-600 dark:text-slate-300
                hover:text-slate-900 dark:hover:text-white transition-all duration-200 disabled:opacity-50"
        >
            <FontAwesomeIcon icon={faTimes} className="w-4 h-4"/>
        </button>
    </div>
);

/**
 * Hộp thoại dùng chung.
 *
 * LƯU Ý QUAN TRỌNG: luôn render qua createPortal ra document.body. Các thẻ
 * glass (`glass-card`, `glass-toolbar`…) dùng `backdrop-filter`, mà phần tử có
 * backdrop-filter sẽ trở thành containing block cho con `position: fixed` →
 * modal render bên trong thẻ sẽ bị "nhốt" trong thẻ đó thay vì phủ toàn màn
 * hình. Portal là cách duy nhất tránh được lỗi này.
 */
export const Modal: React.FC<ModalProps> = ({
                                                isOpen,
                                                onClose,
                                                children,
                                                showHeader = false,
                                                title,
                                                subtitle,
                                                size = 'lg',
                                                mobile = 'fullscreen',
                                                headerActions,
                                                footer,
                                                bare = false,
                                                layer = 'base',
                                                showClose = true,
                                                labelledBy,
                                            }) => {
    // Handle escape key press
    const handleEscape = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useBodyScrollLock(isOpen);
    // Nút Back trên điện thoại đóng modal thay vì rời trang
    useOverlayBackButton(isOpen, onClose);

    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, handleEscape]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-2xl',
        full: 'sm:max-w-5xl',
    };

    // Từ `sm` trở lên ba chế độ hội tụ về cùng một thẻ nổi giữa màn hình;
    // khác nhau chỉ nằm ở phần dưới ngưỡng đó.
    /* `.h-cap-90` thay cho `max-h-[90vh]`, và `.safe-*` để lớp phủ né tai thỏ /
       vạch Home của iPhone. Cả hai đều là lỗi chỉ thấy trên máy thật: `90vh` của
       iOS đo theo màn hình lúc ĐÃ ẨN thanh công cụ Safari, nên khi thanh đang
       hiện thì đáy lớp phủ - chỗ đặt nút Lưu - nằm khuất dưới nó. Xem index.css. */
    const shellByMode = {
        fullscreen: `fixed inset-0 sm:relative sm:inset-auto w-full ${sizeClasses[size]} h-full sm:h-auto sm:h-cap-90
           safe-inset-y rounded-none sm:rounded-xl`,
        sheet: `relative w-full ${sizeClasses[size]} h-cap-90 safe-bottom-mobile rounded-t-2xl sm:rounded-xl`,
        dialog: `relative w-full max-w-[calc(100vw-2rem)] ${sizeClasses[size]} h-cap-90 rounded-xl`,
    };

    const alignByMode = {
        fullscreen: 'sm:items-center sm:p-4',
        sheet: 'items-end sm:items-center sm:p-4',
        dialog: 'items-center p-4',
    };

    const shell = `${shellByMode[mobile]} flex flex-col`;
    const hasHeader = showHeader || !!title;

    return createPortal(
        <div
            className={`fixed inset-0 ${layer === 'top' ? 'z-[120]' : 'z-[100]'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy || (title ? 'modal-title' : undefined)}
        >
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-slate-900/50 sm:backdrop-blur-sm animate-fade-in"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Modal Container */}
            <div className="fixed inset-0 overflow-y-auto">
                <div className={`flex min-h-full justify-center ${alignByMode[mobile]}`}>
                    <div
                        className={`${shell} bg-white/95 dark:bg-slate-900/95 sm:bg-white/90 sm:dark:bg-slate-900/85
                            sm:backdrop-blur-xl sm:border sm:border-white/60 sm:dark:border-white/10
                            shadow-2xl overflow-hidden
                            ${mobile === 'sheet' ? 'animate-slide-up sm:animate-scale-in' : 'animate-scale-in'}`}
                    >
                        {/* Tay nắm - dấu hiệu quen thuộc "kéo xuống để đóng" của
                            bảng trượt. Chỉ trang trí, việc đóng nằm ở nút quay
                            lại trong ModalHeader, nền mờ và nút Back của máy. */}
                        {mobile === 'sheet' && (
                            <div className="sm:hidden pt-2 pb-1 flex justify-center flex-shrink-0" aria-hidden="true">
                                <span className="w-10 h-1 rounded-full bg-slate-300 dark:bg-white/20"/>
                            </div>
                        )}

                        {hasHeader ? (
                            <ModalHeader
                                title={title}
                                subtitle={subtitle}
                                onClose={onClose}
                                actions={headerActions}
                                titleId="modal-title"
                            />
                        ) : showClose && (
                            <button
                                onClick={onClose}
                                aria-label="Đóng"
                                className="absolute right-4 top-4 z-10 w-9 h-9 flex items-center justify-center
                                    rounded-full glass-btn text-slate-600 dark:text-slate-300
                                    hover:text-slate-900 dark:hover:text-white transition-all duration-200 shadow-sm"
                            >
                                <FontAwesomeIcon icon={faTimes} className="w-4 h-4"/>
                            </button>
                        )}

                        <div className={`flex-1 overflow-y-auto overscroll-contain ${bare ? '' : 'p-4 sm:p-6'}`}>
                            {children}
                        </div>

                        {footer && (
                            <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-t border-slate-200/70 dark:border-white/10
                                bg-white/80 dark:bg-slate-900/80">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};
